import { useEffect, useState } from 'react';
import { adminService } from '@/services/adminService';
import { UserDetails } from '@/types/admin';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Download, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface UserDetailModalProps {
  userId: string;
  isOpen: boolean;
  onClose: () => void;
}

export function UserDetailModal({ userId, isOpen, onClose }: UserDetailModalProps) {
  const [details, setDetails] = useState<UserDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [expandedEvents, setExpandedEvents] = useState<Set<number>>(new Set());
  const [expandedProgress, setExpandedProgress] = useState<Set<number>>(new Set());
  const [expandedTimeline, setExpandedTimeline] = useState<Set<number>>(new Set());

  const toggleEventExpanded = (idx: number) => {
    setExpandedEvents(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const toggleProgressExpanded = (idx: number) => {
    setExpandedProgress(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const toggleTimelineExpanded = (idx: number) => {
    setExpandedTimeline(prev => {
      const next = new Set(prev);
      if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  useEffect(() => {
    if (isOpen && userId) {
      loadUserDetails();
    }
  }, [userId, isOpen]);

  const loadUserDetails = async () => {
    setLoading(true);
    try {
      const data = await adminService.getUserDetails(userId);
      setDetails(data);
    } catch (error) {
      console.error('Failed to load user details:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    if (!userId || !details) return;
    const data = await adminService.exportUserData(userId);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `user-${details.email}-export.json`;
    a.click();
  };

  if (loading || !details) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <div className="text-center py-12">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
            <p className="text-gray-600">Loading user details...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-xl">User Details: {details.email}</DialogTitle>
            <Button size="sm" variant="outline" onClick={handleExport}>
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
          </div>
        </DialogHeader>

        <Tabs defaultValue="profile" className="w-full mt-4">
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="profile">Profile</TabsTrigger>
            <TabsTrigger value="activity">Activity ({details.totalEvents})</TabsTrigger>
            <TabsTrigger value="progress">Progress ({details.lessonsCompleted})</TabsTrigger>
            <TabsTrigger value="timeline">Timeline</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">Email</h4><p className="text-gray-900">{details.email}</p></div>
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">Name</h4><p className="text-gray-900">{[details.firstName, details.lastName].filter(Boolean).join(' ') || 'Not set'}</p></div>
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">Tech Comfort</h4><p className="text-gray-900">{details.techComfort || 'Not set'}</p></div>
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">AI Experience</h4><p className="text-gray-900">{details.aiExperience || 'Not set'}</p></div>
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">Organization</h4><p className="text-gray-900">{details.organizationName || 'Not set'}</p></div>
              <div className="space-y-1"><h4 className="text-sm font-semibold text-gray-500">Created</h4><p className="text-gray-900">{new Date(details.createdAt).toLocaleString()}</p></div>
            </div>
          </TabsContent>

          <TabsContent value="activity" className="space-y-2 mt-4">
            <div className="mb-4 text-sm text-gray-600">
              Showing {details.recentEvents.length} most recent events
            </div>
            {details.recentEvents.map((event, idx) => {
              const hasMetadata = event.metadata && Object.keys(event.metadata).length > 0;
              const isExpanded = expandedEvents.has(idx);

              return (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <span className="font-semibold text-gray-900 capitalize">
                        {event.eventType.replace(/_/g, ' ')}
                      </span>
                      {event.eventName && event.eventName !== event.eventType && (
                        <span className="ml-2 text-sm text-gray-600">• {event.eventName}</span>
                      )}
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                      {new Date(event.createdAt).toLocaleString()}
                    </span>
                  </div>

                  <div className="space-y-1 text-sm">
                    {event.pagePath && (
                      <div className="text-gray-600">
                        <span className="font-medium">Page:</span> {event.pagePath}
                      </div>
                    )}
                    {event.elementId && (
                      <div className="text-gray-600">
                        <span className="font-medium">Element:</span> {event.elementId}
                      </div>
                    )}
                    {hasMetadata && (
                      <button
                        onClick={() => toggleEventExpanded(idx)}
                        className="mt-2 flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {isExpanded ? 'Hide' : 'Show'} Details ({Object.keys(event.metadata).length} fields)
                      </button>
                    )}
                    {hasMetadata && isExpanded && (
                      <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                        <div className="text-xs font-medium text-gray-500 mb-2">Additional Data:</div>
                        <div className="text-xs text-gray-700 font-mono whitespace-pre-wrap">
                          {JSON.stringify(event.metadata, null, 2)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
            {details.recentEvents.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 font-medium">No activity recorded</p>
                <p className="text-sm text-gray-400 mt-1">Events will appear here as the user interacts with the platform</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="progress" className="space-y-2 mt-4">
            <div className="mb-4 text-sm text-gray-600">
              Showing {details.recentProgress.length} content items
            </div>
            {details.recentProgress.map((progress, idx) => {
              const statusColors = {
                completed: 'bg-green-100 text-green-800 border-green-200',
                in_progress: 'bg-blue-100 text-blue-800 border-blue-200',
                not_started: 'bg-gray-100 text-gray-800 border-gray-200',
                paused: 'bg-yellow-100 text-yellow-800 border-yellow-200'
              };
              const statusColor = statusColors[progress.status as keyof typeof statusColors] || statusColors.not_started;

              return (
                <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200 hover:border-gray-300 transition-colors">
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex-1">
                      <div className="font-semibold text-gray-900">{progress.contentId}</div>
                      <div className="text-xs text-gray-500 mt-1 capitalize">{progress.contentType}</div>
                    </div>
                    <span className={`text-xs px-3 py-1 rounded-full border font-medium ${statusColor}`}>
                      {progress.status.replace(/_/g, ' ')}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Progress:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-blue-500 transition-all duration-300"
                            style={{ width: `${progress.progressPercentage}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-gray-700">{progress.progressPercentage}%</span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">Time Spent:</span>
                      <div className="font-medium text-gray-900 mt-1">
                        {Math.floor(progress.timeSpentSeconds / 60)} min
                      </div>
                    </div>
                  </div>

                  <div className="mt-3 pt-3 border-t border-gray-200 space-y-1 text-xs text-gray-500">
                    {progress.firstAccessedAt && (
                      <div>Started: {new Date(progress.firstAccessedAt).toLocaleString()}</div>
                    )}
                    <div>Last Activity: {new Date(progress.lastAccessedAt).toLocaleString()}</div>
                    {progress.completedAt && (
                      <div className="text-green-600 font-medium">Completed: {new Date(progress.completedAt).toLocaleString()}</div>
                    )}
                  </div>

                  {progress.progressData && Object.keys(progress.progressData).length > 0 && (
                    <>
                      <button
                        onClick={() => toggleProgressExpanded(idx)}
                        className="mt-2 flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        {expandedProgress.has(idx) ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                        {expandedProgress.has(idx) ? 'Hide' : 'Show'} Progress Data ({Object.keys(progress.progressData).length} fields)
                      </button>
                      {expandedProgress.has(idx) && (
                        <div className="mt-2 p-3 bg-white rounded border border-gray-200">
                          <div className="text-xs font-medium text-gray-500 mb-2">Progress Data:</div>
                          <div className="text-xs text-gray-700 font-mono max-h-40 overflow-y-auto whitespace-pre-wrap">
                            {JSON.stringify(progress.progressData, null, 2)}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              );
            })}
            {details.recentProgress.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 font-medium">No progress recorded</p>
                <p className="text-sm text-gray-400 mt-1">Progress will appear here as the user completes lessons</p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="timeline" className="space-y-2 mt-4">
            <div className="mb-4 text-sm text-gray-600">
              Showing {details.activityTimeline.length} total activities (events + progress)
            </div>
            <div className="space-y-3">
              {details.activityTimeline.slice(0, 50).map((activity, idx) => {
                const isEvent = activity.type === 'event';
                const bgColor = isEvent ? 'bg-purple-50' : 'bg-blue-50';
                const borderColor = isEvent ? 'border-purple-200' : 'border-blue-200';
                const icon = isEvent ? '📊' : '📈';
                const hasMetadata = activity.metadata && Object.keys(activity.metadata).length > 0;
                const isExpanded = expandedTimeline.has(idx);

                return (
                  <div key={idx} className={`p-4 rounded-lg border ${bgColor} ${borderColor} hover:opacity-90 transition-opacity`}>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex items-start gap-2">
                        <span className="text-lg">{icon}</span>
                        <div>
                          <div className="font-semibold text-gray-900">{activity.description}</div>
                          <div className="text-xs text-gray-500 mt-1 capitalize">
                            {activity.type} • {activity.subtype}
                          </div>
                        </div>
                      </div>
                      <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                        {new Date(activity.timestamp).toLocaleString()}
                      </span>
                    </div>

                    {hasMetadata && (
                      <>
                        <button
                          onClick={() => toggleTimelineExpanded(idx)}
                          className="mt-2 flex items-center gap-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                        >
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {isExpanded ? 'Hide' : 'Show'} Details ({Object.keys(activity.metadata).length} fields)
                        </button>
                        {isExpanded && (
                          <div className="mt-2 p-3 bg-white/70 rounded border border-gray-200">
                            <div className="text-xs font-medium text-gray-500 mb-2">Details:</div>
                            <div className="text-xs text-gray-700 space-y-1">
                              {Object.entries(activity.metadata).map(([key, value]) => (
                                <div key={key} className="flex gap-2">
                                  <span className="font-medium min-w-[100px]">{key}:</span>
                                  <span className="flex-1 font-mono">{typeof value === 'object' ? JSON.stringify(value) : String(value)}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}
            </div>
            {details.activityTimeline.length === 0 && (
              <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                <p className="text-gray-500 font-medium">No timeline activity</p>
                <p className="text-sm text-gray-400 mt-1">Activity will appear here as the user uses the platform</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
